import { COLORS } from '@bumble/app/theme';
import CellTowerOutlinedIcon from '@mui/icons-material/CellTowerOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import { Box, Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, Theme, styled, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAccount } from '@bumble/app/hooks/use-account';

const navItems: (Omit<NavFoldableItemProps, 'showSideBar'> | NavItemProps)[] = [
  {
    label: 'Intelligent Edge',
    icon: <CellTowerOutlinedIcon />,
    children: [
      {
        label: 'Dashboards',
        path: '/main-dashboard',
        access: ['account', 'iam_user'],
      },
      {
        label: 'Internet NIDs',
        path: '/routers',
        access: ['account', 'iam_user'],
      },
      {
        label: 'Metro Ethernet NIDs',
        path: '/ethernet-devices',
        access: ['account', 'iam_user'],
      }
    ],
  },
  // {
  //   label: 'Global Private Link',
  //   icon: <PublicOutlinedIcon />,
  //   children: [
  //     {
  //       label: 'Dashboard',
  //       path: '/dashboard',
  //       access: ['account', 'iam_user'],
  //     },
  //     {
  //       label: 'App Services',
  //       path: '/app-services',
  //       access: ['account', 'iam_user'],
  //     },
  //     {
  //       label: 'Endpoints',
  //       path: '/endpoints',
  //       access: ['account', 'iam_user'],
  //     },
  //     {
  //       label: 'Service Nodes',
  //       path: '/service-nodes',
  //       access: ['account', 'iam_user'],
  //     },
  //     {
  //       label: 'Endpoint Nodes',
  //       path: '/endpoint-nodes',
  //       access: ['account', 'iam_user'],
  //     },
  //     {
  //       label: 'User Agents',
  //       path: '/user-agents',
  //       access: ['account', 'iam_user'],
  //     },
  //   ],
  // },
  {
    label: 'Systems',
    icon: <SettingsSuggestOutlinedIcon />,
    children: [
      {
        label: 'Entities',
        path: '/entities',
        access: ['account', 'iam_user']
      },
      {
        label: 'IAM',
        path: '/iam',
        access: ['account', 'iam_user'],
      },
      {
        label: 'Contacts',
        path: '/contacts',
        access: ['account', 'iam_user'],
      },
      {
        label: 'Operations',
        path: '/operations',
        access: ['account', 'iam_user'],
      },
      {
        label: 'Change Logs',
        path: '/histories',
        access: ['account', 'iam_user'],
      },
    ],
  },
];

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  position: 'relative',
  transition: theme.transitions.create('all', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('all', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  position: 'relative',
  width: '70px',
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    // width: drawerWidth,
    flexShrink: 0,
    height: '100%',
    whiteSpace: 'nowrap',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

interface NavItemProps {
  label: string;
  icon?: JSX.Element;
  path: string;
  showSideBar: boolean;
  isSub?: boolean;
}
const NavItem = ({ label, icon, path, showSideBar, isSub }: NavItemProps) => {
  const theme = useTheme();
  return (
    <ListItem key={label}
      disablePadding
      sx={{
        margin: '0 0',
        '& .active': {
          backgroundColor: isSub ? "rgb(234,191, 78)!important" : ""
        },
      }}
    >
      <ListItemButton
        sx={{
          minHeight: isSub ? 40 : 48,
          // px: 2.5,
          p: 0,
          transition: theme.transitions.create('all', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          color: '#364152',
          borderRadius: '8px',
          '& .MuiListItemIcon-root': {
            color: '#364152',
            borderRadius: '28px',
            padding: '8px',
            '> svg': {
              transition: theme.transitions.create('all', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              fontSize: showSideBar ? '1.8rem' : '2.4rem',
            },
          },
          '&:hover': {
            color: theme.palette.primary.main,
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.main,
            },
          },
          '&.active': {
            color: theme.palette.primary.main,
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.main,
            }
          },
          '& .MuiListItemText-root .MuiTypography-root': {
            marginLeft: isSub ? (showSideBar ? '34px' : '24px') : '0',
            fontSize: isSub ? '1.4rem' : '1.6rem',
          },
        }}
        title={label}
        component={NavLink}
        to={path}
      >
        {icon && (<ListItemIcon
          sx={{
            minWidth: 'auto',
            marginLeft: showSideBar ? '0px' : '5px',
            transition: theme.transitions.create('all', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {icon}
        </ListItemIcon>)}
        <ListItemText
          primary={label}
          sx={{
            flex: 'none',
            transition: theme.transitions.create('opacity', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            width: showSideBar ? 'auto' : '0px',
            opacity: showSideBar ? 1 : 0,
            marginLeft: showSideBar ? '8px' : '0px',
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

interface SubItemProps {
  label: string;
  path: string;
  access: string[];
}
interface NavFoldableItemProps {
  label: string;
  icon: JSX.Element;
  children: SubItemProps[],
  showSideBar: boolean;
}
const NavFoldableItem = ({ label, icon, children, showSideBar }: NavFoldableItemProps) => {
  const [open, setOpen] = useState(true);
  const theme = useTheme();
  const { session } = useAccount();

  const toggle = () => {
    setOpen(!open);
  };

  return (
    <List key={label} disablePadding sx={{ margin: '10px 0' }}>
      <ListItemButton
        title={label}
        onClick={toggle}
        sx={{
          minHeight: 48,
          p: 0,
          transition: theme.transitions.create('all', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          color: '#364152',
          borderRadius: '8px',
          '& .MuiListItemIcon-root': {
            color: '#364152',
            borderRadius: '28px',
            padding: '8px',
            '> svg': {
              transition: theme.transitions.create('all', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              fontSize: showSideBar ? '1.8rem' : '2.4rem',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 'auto',
            marginLeft: showSideBar ? '0px' : '5px',
            transition: theme.transitions.create('all', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          sx={{
            flex: 'none',
            transition: theme.transitions.create('opacity', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            width: showSideBar ? 'calc(100% - 72px)' : '0px',
            opacity: showSideBar ? 1 : 0,
            marginLeft: showSideBar ? '8px' : '0px',
          }}
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {children.map(({ label, path, access }) =>
          access.includes(session?.session_type || 'account') && (
            <NavItem
              key={label}
              label={label}
              path={path}
              showSideBar={showSideBar}
              isSub={true}
            />
          ),
        )}
      </Collapse>
    </List>
  );
};

interface SideBarProps {
  hideNavbar?: boolean;
  showSideBar?: boolean;
}
const SideBar = ({ hideNavbar = false, showSideBar = true }: SideBarProps) => {
  // const { token } = useAuthContext();
  const theme = useTheme();
  const { session } = useAccount();

  if (hideNavbar /* || !token */) {
    return null;
  }
  return (
    <Box
      sx={{
        padding: '10px 0px 0px 10px',
        height: 'calc(100vh - 64px)',
        marginTop: '64px',
        width: showSideBar ? drawerWidth : '80px',
        flexShrink: 0,
        background: COLORS.WHITE,
        transition: theme.transitions.create('all', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Drawer
        open={showSideBar}
        variant="permanent"
        sx={{
          top: 0,
          position: 'fixed',
          marginTop: '64px',
          width: showSideBar ? drawerWidth - 10 : '70px',
          [`& .MuiDrawer-paper`]: {
            width: '100%',
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        <Box sx={{ overflow: 'visible', width: '100%' }}>
          <List>
            {navItems.map((navItem) => 'children' in navItem ?
              <NavFoldableItem
                key={navItem.label}
                label={navItem.label}
                icon={navItem.icon}
                children={navItem.children}
                showSideBar={showSideBar}
              /> :
              <NavItem key={navItem.label}
                label={navItem.label}
                icon={navItem.icon}
                path={navItem.path}
                showSideBar={showSideBar}
                isSub={navItem.isSub}
              />
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SideBar;
